import { IconSelector, IconX } from "@tabler/icons-react";
import clsx from "clsx";
import ReactSelect, {
	components,
	type ClearIndicatorProps,
	type DropdownIndicatorProps,
	type OptionProps,
	type MenuPlacement,
	type SingleValue,
} from "react-select";

type SelectOption = {
	value?: string | number | null;
	label: string;
};

type SelectProps = {
	placeholder?: string;
	portal?: boolean;
	className?: string;
	clearable?: boolean;
	disabled?: boolean;
	options: SelectOption[];
	minHeight?: number;
	maxHeight?: number;
	onValueChange?: (value: SelectOption["value"]) => void;
	defaultValue?: SelectOption["value"];
	isUnstyled?: boolean;
};

const ClearIndicator = (props: ClearIndicatorProps<SelectOption, false>) => (
	<components.ClearIndicator {...props}>
		<IconX size={18} stroke={1.5} />
	</components.ClearIndicator>
);

const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, false>) => (
	<components.DropdownIndicator {...props}>
		<IconSelector size={18} stroke={1.5} />
	</components.DropdownIndicator>
);

const Option = (props: OptionProps<SelectOption, false>) => <components.Option {...props} />;

export default function Select({ placeholder, clearable = true, minHeight = 300, maxHeight = 400, portal = true, ...props }: SelectProps) {
	const defaultValue = props.options.find((option) => option.value === props.defaultValue);

	const selectOptions = {
		defaultOptions: true,
		value: defaultValue,
		options: props.options,
		isClearable: clearable,
		isDisabled: props.disabled,
		dropdownIndicator: "yes",
		minMenuHeight: minHeight,
		maxMenuHeight: maxHeight,
		menuPlacement: "auto" as MenuPlacement,
		onChange: (option: SingleValue<SelectOption>) => {
			if (props.onValueChange) {
				props.onValueChange(option?.value || null);
			}
		},
	};

	return (
		<ReactSelect
			key={props.defaultValue}
			components={{ ClearIndicator, DropdownIndicator, Option }}
			placeholder={placeholder}
			classNames={
				props.isUnstyled
					? {
							control: ({ isDisabled }) =>
								clsx(
									"flex flex-row w-full min-h-[38px] max-h-[38px] h-[38px] pl-3 pr-0 gap-3 text-base !rounded-none",
									isDisabled
										? "!bg-muted !border-0 !text-muted-foreground !cursor-not-allowed"
										: "!bg-background !border-transparent focus-within:!border-focus-border focus-within:!ring-0 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
								),
							valueContainer: () => "!p-0",
							placeholder: () => "!text-muted-foreground",
							singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
							menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
							option: ({ isFocused, isSelected }) =>
								clsx(
									"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
									isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
								),
							multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded-md !bg-primary-100 ",
							multiValueLabel: () => "!text-sm !text-primary-600",
							multiValueRemove: () => "!text-sm !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
							dropdownIndicator: () => "!text-muted-foreground",
							clearIndicator: () => "!text-muted-foreground !cursor-pointer",
						}
					: {
							control: ({ isDisabled }) =>
								clsx(
									"flex flex-row w-full min-h-[38px] max-h-[38px] h-[38px] pl-3 pr-0 gap-3 text-base !rounded-md",
									isDisabled
										? "!bg-muted !border-border !text-muted-foreground !cursor-not-allowed"
										: "!bg-background !border-border focus-within:!border-focus-border focus-within:!ring-2 focus-within:!ring-focus-ring/30 !ring-offset-0 !outline-0",
								),
							valueContainer: () => "!p-0",
							placeholder: () => "!text-muted-foreground",
							singleValue: ({ isDisabled }) => clsx(isDisabled ? "!text-muted-foreground" : "!text-foreground"),
							menu: () => "!mt-1 !bg-background !border-1 !border-border !rounded-md !z-[90]",
							option: ({ isFocused, isSelected }) =>
								clsx(
									"!px-3 !py-2 !text-base !text-foreground !cursor-pointer",
									isSelected ? "!bg-blue-100 !text-foreground" : isFocused ? "!bg-blue-50" : "!text-foreground",
								),
							multiValue: () => "inline-flex items-center !h-[24px] !max-h-[24px] !p-1 !rounded-md !bg-primary-100 ",
							multiValueLabel: () => "!text-sm !text-primary-600",
							multiValueRemove: () => "!text-sm !text-muted-foreground hover:!text-foreground !bg-transparent cursor-pointer",
							dropdownIndicator: () => "!text-muted-foreground",
							clearIndicator: () => "!text-muted-foreground !cursor-pointer",
						}
			}
			className={clsx("w-full text-base", props.className)}
			{...selectOptions}
		/>
	);
}
